import React, { useEffect } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  Edit,
  Inventory2Outlined,
  OpenInNew,
  Visibility,
  LockOpenOutlined,
  LockOutlined,
  PhonelinkLock,
} from "@mui/icons-material";
import KeyIcon from "@mui/icons-material/Key";
import Icon from "@mdi/react";
import { mdiEmailSync } from "@mdi/js";
import { menuCont, menuItem } from "../../../common/styles/pop-over-menu";
import useMainStore from "../../landing-page/api/store";
import {
  useUserAuthenticatorAdminStatus,
  useSetAuthenticatorRequired,
} from "../../users-facility/api/hooks";

export interface ActionMenuRow {
  applicationUserId: string;
  role: string;
  isAdminLocked: boolean;
  adminLockingReason?: string;
  lastLoginAt?: Date | null;
  name: string;
  photoUrl?: string | null;
}

interface ActionMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;

  row: ActionMenuRow;
  status: "idle" | "pending" | "success" | "error" | string;

  // flags
  CGAccess: boolean;
  CSAccess: boolean;
  hasBizboxAccess?: boolean;
  userRole?: string;
  mainStoreHasClient: boolean;
  walkthroughMenuOpen?: boolean;

  // privileges
  groupAdminPrivileges: { update?: boolean; lock?: boolean; resend?: boolean; delete?: boolean };
  bizboxUserPrivileges: { edit?: boolean; lock?: boolean; resend?: boolean; delete?: boolean };

  isInternal?: boolean;

  // actions
  onEdit: () => void;
  onResendEmail: () => void;
  onLock: () => void;
  onUnlock: () => void;
  onManageAccess: () => void;
  onManageProducts?: () => void;
  onDelete: () => void;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl,
  open,
  onClose,
  row,
  status,
  CGAccess,
  CSAccess,
  hasBizboxAccess,
  userRole,
  mainStoreHasClient,
  walkthroughMenuOpen = false,
  isInternal = false,
  groupAdminPrivileges,
  bizboxUserPrivileges,
  onEdit,
  onResendEmail,
  onLock,
  onUnlock,
  onManageAccess,
  onManageProducts,
  onDelete,
}) => {
  const mainStore = useMainStore();

  const canLock = !!(groupAdminPrivileges.lock || bizboxUserPrivileges.lock);

  /* ------------------ ROLE + HIERARCHY LOGIC ------------------ */

  const currentUser = mainStore.currentUser;
  const currentUserId = currentUser?.id;

  const isSelf = row.applicationUserId === currentUserId;

  const isGroupAdmin = currentUser?.roles?.includes("GroupAdmin");

  const isCoGroupAdmin = currentUser?.roles?.includes("CoGroupAdmin");

  const isSuperAdmin = currentUser?.roles?.includes("SuperAdmin");

  const isBizboxAdmin = currentUser?.roles?.includes("BizboxAdmin");

  // Admin: require / un-require authenticator MFA for this user (feature/totp-mfa).
  // Only for internal (BizBox) users, and only SuperAdmin/BizboxAdmin can toggle it.
  const canManageAuthenticator = isInternal && (isSuperAdmin || isBizboxAdmin);
  const { data: mfa, isLoading: mfaLoading } = useUserAuthenticatorAdminStatus(
    row.applicationUserId,
    open && !!canManageAuthenticator,
  );
  const setRequired = useSetAuthenticatorRequired();
  const authRequired = !!mfa?.required;
  const authNotEligible = mfa != null && !mfa.eligible;
  const handleToggleAuthenticator = () => {
    setRequired.mutate(
      { userId: row.applicationUserId, required: !authRequired },
      { onSuccess: () => onClose() },
    );
  };

  const isTargetGroupAdmin = row.role === "GroupAdmin"

  // 🔒 Hierarchy Restrictions
  const cannotLock =
    isSelf || // nobody can lock themselves
    (isCoGroupAdmin && isTargetGroupAdmin) ||
    (isGroupAdmin && isTargetGroupAdmin);

  const cannotEdit =
    (isCoGroupAdmin && isTargetGroupAdmin) ||
    (isGroupAdmin && isTargetGroupAdmin && !isSelf);

  /* ------------------------------------------------------------- */

  const canResendEmail =
    !row.lastLoginAt &&
    mainStoreHasClient &&
    ((["GroupAdmin", "CoGroupAdmin"].includes(row.role) &&
      groupAdminPrivileges.resend &&
      (isSuperAdmin || isGroupAdmin || isBizboxAdmin)) ||
      (row.role === "BizboxUser" &&
        bizboxUserPrivileges.resend &&
        (isSuperAdmin || isBizboxAdmin)) ||
      (row.role === "BizboxAdmin" &&
        bizboxUserPrivileges.resend &&
        isSuperAdmin));

  // Delete permission matrix — privilege-aware
  // BU006 gates: BizboxUser (BizboxAdmin+SuperAdmin), BizboxAdmin (SuperAdmin only)
  // GA005 gates: CoGroupAdmin (SuperAdmin, BizboxAdmin, GroupAdmin)
  const canDeleteTarget = (() => {
    if (isSelf || !mainStoreHasClient) return false;

    if (row.role === "CoGroupAdmin") {
      // Requires GA005
      if (!groupAdminPrivileges.delete) return false;
      return isSuperAdmin || isBizboxAdmin || isGroupAdmin;
    }

    if (row.role === "BizboxUser") {
      // Requires BU006
      if (!bizboxUserPrivileges.delete) return false;
      return isSuperAdmin || isBizboxAdmin;
    }

    if (row.role === "BizboxAdmin") {
      // Requires BU006, SuperAdmin only
      if (!bizboxUserPrivileges.delete) return false;
      return isSuperAdmin;
    }

    return false; // GroupAdmin and above cannot be deleted
  })();

  const deleteTooltip = (() => {
    if (isSelf || !mainStoreHasClient || canDeleteTarget) return "";
    if (row.role === "GroupAdmin") return "Group Admin cannot be deleted.";
    if (row.role === "CoGroupAdmin" && !groupAdminPrivileges.delete)
      return "You don't have the Delete Co-Admin privilege.";
    if ((row.role === "BizboxUser" || row.role === "BizboxAdmin") && !bizboxUserPrivileges.delete)
      return "You don't have the Delete User privilege.";
    return "You do not have permission to delete this user.";
  })();

  const hasDeletePrivilege = !!(bizboxUserPrivileges.delete || groupAdminPrivileges.delete);

  const items = [
    // View / Edit
    {
      title:
        CGAccess || (CSAccess && row.role === "BizboxAdmin") ? "View" : "Edit",
      icon:
        CGAccess || (CSAccess && row.role === "BizboxAdmin") ? (
          <Visibility />
        ) : (
          <Edit />
        ),
      disabled: cannotEdit || !mainStoreHasClient,
      tooltip: "",
      onClick: onEdit,
    },

    // Delete User
    ...((hasDeletePrivilege || (walkthroughMenuOpen && (isSuperAdmin || isBizboxAdmin)))
      ? [
          {
            title: "Delete User",
            icon: <DeleteOutline />,
            disabled: walkthroughMenuOpen ? false : !canDeleteTarget,
            tooltip: walkthroughMenuOpen ? "" : deleteTooltip,
            onClick: onDelete,
          },
        ]
      : []),

    // Lock / Unlock
    ...(canLock
      ? [
          {
            title: row.isAdminLocked ? "Unlock User" : "Lock User",
            icon:
              status === "pending" ? (
                <CircularProgress size={20} />
              ) : row.isAdminLocked ? (
                <LockOpenOutlined />
              ) : (
                <LockOutlined />
              ),
            disabled:
              cannotLock ||
              CGAccess ||
              (CSAccess && row.role === "BizboxAdmin") ||
              !mainStoreHasClient ||
              status === "pending",

            tooltip: row.isAdminLocked ? (row.adminLockingReason ?? "") : "",
            onClick: row.isAdminLocked ? onUnlock : onLock,
          },
        ]
      : []),

    // Manage Access — SuperAdmin / BizboxAdmin only
    ...((isSuperAdmin || isBizboxAdmin)
      ? [
          {
            title: "Manage Access",
            icon: <KeyIcon sx={{ transform: "rotate(-45deg) scaleX(-1)" }} />,
            disabled: row.role === userRole,
            tooltip: "",
            openInNew: true,
            onClick: onManageAccess,
          },
        ]
      : []),

    // Manage Products — BizBox Users tab only, SuperAdmin / BizboxAdmin only
    ...(isInternal && (isSuperAdmin || isBizboxAdmin) && onManageProducts
      ? [
          {
            title: "Manage Products",
            icon: <Inventory2Outlined />,
            disabled: false,
            tooltip: "",
            openInNew: true,
            onClick: onManageProducts,
          },
        ]
      : []),

    // Resend Email
    ...((canResendEmail || (walkthroughMenuOpen && (isSuperAdmin || isBizboxAdmin)))
      ? [
          {
            title: "Resend Email",
            icon: <Icon path={mdiEmailSync} size={1} />,
            disabled: walkthroughMenuOpen ? false : !mainStoreHasClient,
            tooltip: "",
            onClick: onResendEmail,
          },
        ]
      : []),
  ];

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{ paper: { id: walkthroughMenuOpen ? "user-actions-menu" : undefined, "data-testid": "user-actions-menu", sx: menuCont } as any }}
    >
      {items.map((item, index) => {
        const itemTestId = `user-actions-menu-${item.title.toLowerCase().replace(/\s+/g, "-")}`;
        return (
        <Tooltip
          key={index}
          title={
            item.tooltip ? (
              <Typography fontSize={14}>{item.tooltip}</Typography>
            ) : (
              ""
            )
          }
        >
          <span style={{ display: "block" }}>
            <MenuItem
              id={itemTestId}
              data-testid={itemTestId}
              disabled={item.disabled}
              sx={{ borderRadius: "8px" }}
              onClick={() => {
                item.onClick();
                onClose();
              }}
            >
              <Box sx={{ ...menuItem, justifyContent: "space-between", width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ mr: "10px" }}>{item.icon}</Box>
                  <Typography variant="body2">{item.title}</Typography>
                </Box>
                {(item as any).openInNew && (
                  <OpenInNew sx={{ fontSize: 14, color: "text.secondary", ml: 1 }} />
                )}
              </Box>
            </MenuItem>
          </span>
        </Tooltip>
        );
      })}

      {canManageAuthenticator && (
        <MenuItem
          id="user-actions-menu-require-authenticator"
          data-testid="user-actions-menu-require-authenticator"
          disabled={mfaLoading || setRequired.isPending || authNotEligible}
          sx={{ borderRadius: "8px" }}
          onClick={handleToggleAuthenticator}
        >
          <Box sx={{ ...menuItem, width: "100%" }}>
            <Box sx={{ mr: "10px", display: "flex" }}>
              {mfaLoading || setRequired.isPending ? (
                <CircularProgress size={20} />
              ) : (
                <PhonelinkLock />
              )}
            </Box>
            <Typography variant="body2">
              {authNotEligible
                ? "Authenticator (internal users only)"
                : authRequired
                  ? "Remove authenticator requirement"
                  : "Require authenticator app"}
            </Typography>
          </Box>
        </MenuItem>
      )}
    </Popover>
  );
};
